import { Component, EventEmitter, Injector, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { debounceTime, take } from 'rxjs/operators';
import { MainFormBase } from '../../../../../components/common/main-form-base/main-form-base';
import { WorkspaceBase } from '../../../../../components/common/workspace-base/workspace-base';
import { ReportsWorkspaceComponent } from '../../workspaces/reports-workspace/reports-workspace.component';
import { reportsFormConfig } from './reports-form-config';
import { TranslateService } from '@ngx-translate/core';
import { DropDownListComponent } from '@progress/kendo-angular-dropdowns';
import { PolluxFormConfig } from '../../../../../components/common/pollux-form-config/pollux-form-config';
import { AppStorageService } from '../../../../../services/app-storage.service';

/**
 * Reports form
 */
@Component({
  selector: 'reports-form',
  templateUrl: './reports-form.component.html',
  styleUrls: ['./reports-form.component.css']
})
export class ReportsFormComponent extends MainFormBase<any> implements OnInit, OnDestroy {
  /**
   * The project retriever
   */
  @ViewChild("projectRetriever") public projectRetriever: DropDownListComponent;

  /**
   * The portfolio (VE) retriever
   */
  @ViewChild("portfolioRetriever") public portfolioRetriever: DropDownListComponent;

  /**
   * The property retriever
   */
  @ViewChild("propertyRetriever") public propertyRetriever: DropDownListComponent;

  /**
   * Gets the required retrievers
   */
  protected get requiredRetrievers(): string[] {
    return ['Project', 'ValuationPortfolio', 'User'];
  }

  /**
   * The currency
   */
  public currency: string = null;

  /**
   * The parent
   */
  @Input() parent: any = null;

  /**
   * The unit of length
   */
  public unitOfLength: string = null;

  /**
   * Show the move to the previous item button
   */
  @Input() showPrevBtn = false;

  /**
   * Show the move to the next item button
   */
  @Input() showNextBtn = false;

  /**
   * Emits an event if the move to previous item button was clicked
   */
  @Output() showPrevious = new EventEmitter<void>();

  /**
   * Emits an event if the move to next item button was clicked
   */
  @Output() showNext = new EventEmitter<void>();

  /**
   * Sets the default retriever data
   * @param data
   */
  protected setDefaultRetrieverData(data: any) {
    this.setDefaultRetrieverDataProject(data);

    this.setDefaultRetrieverDataVE(data);
  }

  /**
   * Sets the default retriever data for the project retriever
   * @param data
   */
  private setDefaultRetrieverDataProject(data: any) {
    //Add the 0 entry
    if (!this.retrieverData['Project'].find((en) => en.id === 0)) {
      this.retrieverData['Project'].splice(0, 0, {
        id: 0,
        projectName: ''
      });
    }
    //Add the pre-selected project entry
    if (data && data.Oid > 0) {
      if (!this.retrieverData['Project'].find((en) => en.id === data.ProjectStdId)) {
        this.retrieverData['Project'].splice(1, 0, {
          id: data.ProjectStdId,
          projectName: data.ProjectStdIdDescription
        });
      }
    }
    else if (data && data.Oid == -1) {
      if (this.parent) {
        if (!this.retrieverData['Project'].find((en) => en.id === this.parent.ProjectID)) {
          this.retrieverData['Project'].splice(1, 0, {
            id: this.parent.ProjectID,
            projectName: this.parent.ProjectIDDescription
          });
        }
      }
    }
  }

  /**
   * Sets the default retriever data for the Valuation Event (VE) retriever
   * @param data
   */
  private setDefaultRetrieverDataVE(data: any) {
    //Add the 0 entry
    if (!this.retrieverData['ValuationPortfolio'].find((en) => en.id === 0)) {
      this.retrieverData['ValuationPortfolio'].splice(0, 0, {
        id: 0,
        ValuationPortfolioName: ''
      });
    }
    //Add the pre-selected VE entry
    if (data && data.Oid > 0) {
      if (!this.retrieverData['ValuationPortfolio'].find((en) => en.id === data.ValuationProjectId)) {
        this.retrieverData['ValuationPortfolio'].splice(1, 0, {
          id: data.ValuationProjectId,
          ValuationPortfolioName: data.ValuationProjectIdDescription,
          puid: data.ProjectStdId
        });
      }
    }
    else if (data && data.Oid == -1) {
      if (this.parent) {
        if (!this.retrieverData['ValuationPortfolio'].find((en) => en.id === this.parent.Oid)) {
          this.retrieverData['ValuationPortfolio'].splice(1, 0, {
            id: this.parent.Oid,
            ValuationPortfolioName: this.parent.ValuationPortfolioName + ', ' + this.parent.Oid,
            puid: this.parent.ProjectID
          });
        }
      }
    }
  }

  /**
   * Sets the OID
   */
  @Input() set oid(value: number) {
    this.currentOid = value;
  }

  /**
   * The workspace
   */
  @Input() workspace: ReportsWorkspaceComponent;

  /**
   * The available report templates
   */
  public availableTemplates: any[] = [];

  /**
   * The available templates
   */
  public filteredAvailableTemplates: any[] = [];

  /**
   * The available valuation events
   */
  public filteredValuationPortfolios: any[] = [];

  /**
   * The property list reference data
   */
  public propertiesListReferenceData: any = null;

  /**
   * Properties without a report
   */
  public propertyWithoutReport: any[] = [];

  /**
   * The available languages
   */
  public availableLanguageDefinitions: any[] = [];

  /**
   * The selected project
   */
  private selectedProjectId = -1;

  /**
   * 
   * @param injector
   * @param translateService
   * @param appStorage
   */
  constructor(protected injector: Injector, private translateService: TranslateService,
    private appStorage: AppStorageService,) {
    super(injector);
  }

  /**
   * Gets the workspace reference
   */
  public get workspaceReference(): WorkspaceBase {
    return this.workspace;
  }

  /**
   * Gets the form config
   */
  protected get formConfig(): PolluxFormConfig[] {
    return reportsFormConfig;
  }

  /**
  * Gets the API POST endpoint for saving a single BO
  */
  protected getSaveSingleEndpoint(): string {
    return this.appServiceHelper.getServiceEndpoint('pollux.projectValuation.api', `Reports`);
  }

  /**
  * Gets the API GET endpoint for loading a single BO by it's OID
  * @param oid The BO's OID
  */
  protected getLoadSingleEndpoint(oid: number): string {
    return this.appServiceHelper.getServiceEndpoint('pollux.projectValuation.api', `Reports?oid=${oid}`);
  }

  /**
  * Gets the API GET endpoint for loading report templates
  */
  protected getLoadTemplatesEndpoint(): string {
    return this.appServiceHelper.getServiceEndpoint('pollux.reportDesigner.api', `templates/noJson`);
  }

  /**
   * Gets the API GET endpoint for loading languages
   * @returns
   */
  private getLoadLanguageDefinitionEndpoint(): string {
    return this.appServiceHelper.getServiceEndpoint('pollux.dataverse.api', 'dictionary/list')
  }

  /**
   * The required ref lists
   */
  protected get requiredReflists(): string[] {
    return [
      'ref_recordstatus',
      'ref_property_type_l3',
      'ref_valuation_country',
      'ref_report_status',
      'ref_currency_with_code',
      'ref_unit_of_length',
      'ref_inspection_type',
      'ref_l3_argus_report_sync_state',
      'ref_l3_leading_method',
      'ref_l3_display_values_over_time',
      'config_user',
      'config_business_units'
    ];
  }

  /**
   * Closes the form
   */
  public close(): void {
    this.eventBus.broadcastEvent('closePortfolioForm');
  }

  /**
   * Gets the available valuation events
   * @returns
   */
  public getFilteredValuationPortfolios() {
    const projectId = this.formData.controls['ProjectStdId'].value;
    if (projectId > 0) {
      this.filteredValuationPortfolios = this.retrieverData.ValuationPortfolio.filter(ve => ve.puid == projectId);
    } else {
      this.filteredValuationPortfolios = this.retrieverData.ValuationPortfolio
    }
    return this.filteredValuationPortfolios;
  }

  /**
   * Performs post-processing of the data
   * Important: Please do not parse any Dates from string in this method!
   * @param data Loaded data
   */
  protected postProcessData(data: any): Promise<any> {
    return new Promise<any>((resolve, reject) => {
      try {
        const availableTemplates = this.additionalLoadPromisesResult.availableTemplates;
        const selectedSoftDeletedTemplate = availableTemplates.find((item) => { return (item.Oid == data.TemplateId) && (item.InvisibleFlag == 1); });
        if (selectedSoftDeletedTemplate) {
          this.availableTemplates = availableTemplates.filter((item) => { return ((item.Oid != data.TemplateId) && (item.InvisibleFlag != 1)) || (item.Oid == data.TemplateId); });
        }
        else {
          this.availableTemplates = availableTemplates.filter((item) => { return item.InvisibleFlag != 1; });
        }

        this.filteredAvailableTemplates = this.availableTemplates;

        this.availableLanguageDefinitions = this.additionalLoadPromisesResult.availableLanguageDefinitions

        this.filteredValuationPortfolios = this.retrieverData.ValuationPortfolio;

        this.propertiesListReferenceData = {
          Oid: data.Oid,
          ProjectID: data.ProjectID
        };

        if (this.isNew) {
          //in new mode always set filledOnSave (no matter what selectedTabHierarchyItemType is)
          this.setFilledOnSave(-1, data);
          this.setHierarchy(data);
          this.propertyWithoutReport = this.additionalLoadPromisesResult.propertiesWithoutReports.results;
          this.propertyWithoutReport.forEach(p => {
            p.name = `${p.propertyStreet} ${p.propertyStreetNumber}, ${p.postalCode} ${p.municipality}`;
          })
        }

        this.currency = data.CurrencySymbol;
        this.unitOfLength = data.UnitOfLengthDescription;

        if (this.isNew && data.ValuationProjectId > 0) {
          const url = `${this.appServiceHelper.getServiceEndpoint('pollux.projectValuation.api', 'ValuationPortfolios?oid=')}${data.ValuationProjectId}`;
          this.httpClient.get(url).pipe(take(1)).pipe(take(1)).subscribe((result: any) => {
            data.LeadingMethodId = result.LeadingMethodId;
            resolve(data);
          })
        } else {
          resolve(data);
        }


      } catch (e) {
        reject(e);
      }
    });
  }

  /**
   * Sets the hierarchy
   * @param data
   */
  private setHierarchy(data: any) {
    let projectStd = null;

    if (this.workspace?.workplace?.selectedTabHierarchyItemType === "valuationPortfolio") {
      projectStd = this.setHierarchyForValEvent(data, projectStd);
    } else if (this.workspace?.workplace?.selectedTabHierarchyItemType === "report") {
      //Bug 2149: Reports - Property Selection
      projectStd = this.setHierarchyForReport(data, projectStd);
    }

    if (projectStd !== null) {
      this.setHierarchyForProject(projectStd, data);
    }
  }

  /**
   * Sets the hierarchy for valuation events
   * @param data
   * @param projectStd
   * @returns
   */
  private setHierarchyForValEvent(data: any, projectStd: any): any {
    const parentRow = this.workspace.workplace.selectedTabHierarchyItem;
    if (parentRow) {
      data.ValuationProjectId = parentRow.Oid || 0;
      data.ValuationProjectIdDescription = parentRow.ValuationPortfolioName || "";
      data.ProjectStdId = parentRow.ProjectID || 0;
      data.ProjectStdIdDescription = parentRow.ProjectIDDescription || "";
      data.LanguageId = parentRow.LanguageId || 0;
      data.TemplateId = parentRow.TemplateId || 0;
      data.ReportClientFreeText = parentRow.ClientManual || "";
      projectStd = parentRow.ProjectID;
    }
    return projectStd;
  }

  /**
   * Sets the hierarchy for reports
   * @param data
   * @param projectStd
   * @returns
   */
  private setHierarchyForReport(data: any, projectStd: any): any {
    const parentRow = this.workspace.workplace.main.selectedTabHierarchyItem;
    if (parentRow) {
      data.ValuationProjectId = parentRow.ValuationProjectId || 0;
      data.ValuationProjectIdDescription = parentRow.ValuationProjectIdDescription || "";
      data.ProjectStdId = parentRow.ProjectStdId || 0;
      data.ProjectStdIdDescription = parentRow.ProjectStdIdDescription || "";
      projectStd = parentRow.ProjectStdId;

    }
    return projectStd;
  }

  /**
   * Sets the hierarchy for projects
   * @param projectStd
   * @param data
   */
  private setHierarchyForProject(projectStd: any, data: any): void {
    const selectedProject = this.retrieverData.Project.find(p => p.id === projectStd);
    this.filteredValuationPortfolios = this.retrieverData.ValuationPortfolio.filter(ve => ve.puid == projectStd);
    if (selectedProject) {
      data.localCurrencyId = this.reflistData.ref_currency_with_code.find(c => c.additionalDescription === selectedProject.currencySymbol)?.oid;
      data.reportCurrencyId = this.reflistData.ref_currency_with_code.find(c => c.additionalDescription === selectedProject.currencySymbol)?.oid;

      data.localUnitOfLengthId = this.reflistData.ref_unit_of_length.find(c => c.description === selectedProject.unitOfLengthDescription)?.oid;
      data.reportUnitOfLengthId = this.reflistData.ref_unit_of_length.find(c => c.description === selectedProject.unitOfLengthDescription)?.oid;
    }
  }

  /**
   * Sets the 'filled on save' info
   * @param propertyId
   * @param data
   */
  private setFilledOnSave(propertyId: number, data) {
    if (data != null) {
      if (propertyId != data.PropertyRegionDescription) {
        data.PropertyRegionDescription = this.translateService.instant('filledOnSave');
        data.PropertyPostcode = this.translateService.instant('filledOnSave');
        data.PropertyName = this.translateService.instant('filledOnSave');
        data.PropertyTypeIdFilledOnSave = true;
        data.PropertyCountryDescriptionFilledOnSave = true;
        data.PropertyStreet = this.translateService.instant('filledOnSave');
      }
    }
    else if (this.formData && propertyId != this.formData.controls['PropertyRegionDescription'].value) {
      this.formData.controls['PropertyRegionDescription'].setValue(this.translateService.instant('filledOnSave'));
      this.formData.controls['PropertyMunicapelDescription'].setValue(this.translateService.instant('filledOnSave'));
      this.formData.controls['PropertyPostcode'].setValue(this.translateService.instant('filledOnSave'));
      this.formData.controls['PropertyName'].setValue(this.translateService.instant('filledOnSave'));
      this.formData.controls['PropertyTypeIdFilledOnSave'].setValue(true);
      this.formData.controls['PropertyCountryDescriptionFilledOnSave'].setValue(true);
      this.formData.controls['PropertyStreet'].setValue(this.translateService.instant('filledOnSave'));
    }
  }

  /**
   * Handles a property selection
   * @param $event
   * @param data
   */
  onPropertySelected($event, data = null) {
    this.setFilledOnSave($event, data);
  }

  /**
   * Called when the dataset status was updated
   * @param newRecordStatusId
   */
  public datasetStatusUpdated(newRecordStatusId): void {
    const formReadOnly = newRecordStatusId == 2;

    this.setReadOnly(formReadOnly);
  }

  /**
   * Gets properties without reports
   * @param searchterm
   */
  private getPropertyWithoutReports(searchterm: string) {
    const projectId = this.formData.controls['ProjectStdId'].value;
    const url = `${this.appServiceHelper.getServiceEndpoint('pollux.api', 'dxsRetriever')}/PropertyWithoutReport?searchTerm=${searchterm}&valuationProjectId=0`;
    const filters = projectId > 0 ? [{ 'name': 'ProjectStdID', 'value': projectId }] : null
    this.httpClient.post(url, filters).pipe(take(1)).subscribe((data: any) => {
      this.propertyWithoutReport = data.results;
      this.propertyWithoutReport.forEach(p => {
        p.name = `${p.propertyStreet} ${p.propertyStreetNumber}, ${p.postalCode} ${p.municipality}`;
      })
    });
  }

  /**
   * Disables the Argus Property Asset ID for non-admins
   */
  private setArgusPropertyAssetIdIsEditable() {
    this.formConfig.find(formConfigElement => formConfigElement.name == 'ReportArgusImportPropertyAssetId').disabled = !this.appStorage.userIsInRole(['Admin']);
  }

  /**
   * If a validation error occurs, this ethd is called.
   * Use it to inform the user, enable buttons, etc
   * @param _data The data
   */
  public handleValidationError(_data: any) {
    this.eventBus.broadcastEvent("openConfirmDialog", {
      hideConfirmButton: true,
      cancel: () => {
        //Nothing
      }
    }, 'fillRequiredFieldsDialog');
  }

  /**
   * Evaluates the save result. Returns true if saving was successful
   * @param result The save result
   */
  public evaluateSaveResult(result: any): boolean {
    return result?.m_Item1;
  }

  /**
   * Perform any pre-save activities here, for example user interaction, user queries etc.
   * If the Promise is resolved with true, saving will continue. If iit's resolved with false or rejected, saving is cancelled.
   * @param _data The data that should be saved
   */
  public handlePreSave(data: any): Promise<boolean> {
    return new Promise((resolve) => {
      const leadingMethodInvalid = this.formData.controls['LeadingMethodId'].invalid
      if (leadingMethodInvalid) {
        this.eventBus.broadcastEvent("openConfirmDialog", {
          success: () => {
            resolve(false);
          }
        }, 'leadingMethodNotFilled');
      }
      else {
        resolve(true);
      }
    });
  }

  /**
   * If the save process was aborted, this methd is called.
   * You can use it to re-enable buttons, inform the user, etc
   * @param _data The data that should be saved originally
   */
  public handleSaveAborted(_data: any): void {
    this.eventBus.broadcastEvent('enableSaveButtonIfRequired');
  }

  /**
   * If the save call was triggered, this method is called.
   * Use it to inform the user, close the form or trigger grid reloading.
   * @param saveResult Th result returned from the API
   */
  protected handlePostSave(saveResult: any): void {
    this.eventBus.broadcastEvent('reportsDataSaved', saveResult, this.currentOid === -1);
    this.close();
  }

  /**
   * If an error occurred during saving, this method is called.
   * Use it to re-enable buttons, inform the user, etc.
   * Note: This occurs on errors and doesn't evaluate the saveResult returned from the API, since this can be different between calls.
   * @param error The error information
   */
  protected handleSaveError(error: any): void {

    alert('An error occurred while saving your data!');
    console.error(error);
    this.eventBus.broadcastEvent('enableSaveButtonIfRequired');
  }

  /**
   * The next button was clicked
   */
  public next(): void {
    this.showNext.emit();
  }

  /**
   * The previous button was clicked
   */
  public previous(): void {
    this.showPrevious.emit();
  }

  /**
   * Initializes the component
   */
  ngOnInit(): void {
    this.onEvents.push(this.eventBus.subscribeEvent('saveReportChanges', () => {
      this.save();
    }));

    this.onEvents.push(this.eventBus.subscribeEvent('valuationTabSelect', () => {
      this.close();
    }));

    const availableTemplatesUrl = this.getLoadTemplatesEndpoint();

    this.getObservableOnLoad('availableTemplates', this.httpClient.get(availableTemplatesUrl).pipe(take(1)));
    this.getObservableOnLoad('availableLanguageDefinitions', this.httpClient.get(this.getLoadLanguageDefinitionEndpoint()).pipe(take(1)));

    if (this.isNew) {
      const url = `${this.appServiceHelper.getServiceEndpoint('pollux.api', 'dxsRetriever')}/PropertyWithoutReport?searchTerm=%%%%&valuationProjectId=0`;
      this.getObservableOnLoad('propertiesWithoutReports', this.httpClient.post<any[]>(url, null).pipe(take(1)));
    }

    this.setArgusPropertyAssetIdIsEditable();

    this.init().then(() => {
      this.eventBus.broadcastEvent('showSaveCancelNavigationAction', true);

      //inform infobar if required
      this.eventBus.broadcastEvent('project-valuation-infobar-item-changed', {
        mode: 'report',
        item: this.formData.getRawValue(),
        itemIdentityProperty: 'Oid'
      });

      this.datasetStatusUpdated(this.formData.controls['RecordStatusId'].value);

      if (this.isNew) {
        setTimeout(() => {
          if (this.isNew) {
            this.registerRetrieverComponent(this.projectRetriever, 'Project');
            this.registerRetrieverComponent(this.portfolioRetriever, 'ValuationPortfolio');
            this.registerPropertyFilterChanges();
          }
        });
      }
    });
  }

  /**
   * Custom retriever register, since api is different from Retriever retriever api (post and contains real data)
   */
  private registerPropertyFilterChanges() {
    this.subscriptions.add(this.propertyRetriever.filterChange
      .asObservable()
      .pipe(
        debounceTime(500))
      .subscribe((value) => {
        this.getPropertyWithoutReports(value);
      }));
  }

  /**
   * Called when the finalization state was updated
   * @param statusId
   */
  public finalizationStatusUpdated(statusId) {
    if (statusId == 2) {
      this.datasetStatusUpdated(this.formData.controls['RecordStatusId'].value);
    }
  }

  /**
   * Filters templates by language selection
   * @param languageDefinitionId
   */
  public filterTemplatesByLanguage(languageDefinitionId: any) {
    this.filteredAvailableTemplates = this.availableTemplates.filter((template) => {
      return template.templateLanguages.indexOf(parseInt(languageDefinitionId)) > -1;
    });
    const selectedTemplateID = this.formData.controls['TemplateId'].value;
    if (selectedTemplateID && !this.filteredAvailableTemplates.find(t => t.id === selectedTemplateID)) {
      this.formData.controls['TemplateId'].setValue(null);
    }
  }

  /**
   * Updates units, currencies,properties and ve based on selected project
   * @param projectId 
   */
  public onProjectSelected(projectId: any) {
    this.filteredValuationPortfolios = this.retrieverData.ValuationPortfolio.filter(ve => ve.puid == projectId);
    if (this.isNew) {
      this.getPropertyWithoutReports('%%%%');
    }

    const selectedProject = this.retrieverData.Project.find(p => p.id === projectId);

    if (selectedProject) {
      this.formData.controls['localCurrencyId'].setValue(this.reflistData.ref_currency_with_code.find(c => c.additionalDescription === selectedProject.currencySymbol)?.oid);
      this.formData.controls['reportCurrencyId'].setValue(this.reflistData.ref_currency_with_code.find(c => c.additionalDescription === selectedProject.currencySymbol)?.oid);

      this.formData.controls['localUnitOfLengthId'].setValue(this.reflistData.ref_unit_of_length.find(c => c.description === selectedProject.unitOfLengthDescription)?.oid);
      this.formData.controls['reportUnitOfLengthId'].setValue(this.reflistData.ref_unit_of_length.find(c => c.description === selectedProject.unitOfLengthDescription)?.oid);
    }

  }

  /**
   *  Check if proeprties without reports need to be filtered by project
   */
  public onOpenPropertyWithoutReport() {
    const projectId = this.formData.controls['ProjectStdId'].value;
    if (this.isNew && this.selectedProjectId !== projectId) {
      this.selectedProjectId = projectId;
      this.getPropertyWithoutReports('%%%%');
    }
  }
}
